import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiMonitor, FiUpload, FiCheckCircle, FiArrowRight, FiAlertCircle } = FiIcons;

const ServiceTypeSelection = () => {
  const [selectedType, setSelectedType] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const navigate = useNavigate();

  const serviceTypes = [
    {
      id: 'online',
      title: 'Online Services',
      description: 'Complete applications online with document upload',
      icon: FiMonitor,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      features: [
        'Document upload/scan is mandatory',
        'Integrated document scanner',
        'Local file upload support',
        'Complete online process',
        'Real-time application tracking'
      ],
      requirements: [
        'Upload required documents',
        'Have digital copies ready',
        'Stable internet connection'
      ]
    },
    {
      id: 'home',
      title: 'Home Services',
      description: 'Professional operators visit your home to complete applications',
      icon: FiHome,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      features: [
        'Skip document upload step entirely',
        'Professional operators handle everything',
        'Direct payment after service selection',
        'Convenient home visit service',
        'No need to scan or upload documents'
      ],
      requirements: [
        'Be present at home during the visit',
        'Have required documents ready',
        'Provide basic information'
      ]
    }
  ];

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setShowValidation(false); // Clear validation message when user makes a selection
  };

  const handleContinue = () => {
    if (!selectedType) {
      setShowValidation(true);
      toast.error('You must choose either Home Services or Online Services before moving to the next step.');
      return;
    }

    // Store the service type preference
    localStorage.setItem('selectedServiceType', selectedType.id);
    
    // Navigate to service categories
    navigate('/service-categories');
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Choose Your Service Type
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            You must choose either Home Services or Online Services to proceed with your application. This choice will determine your application process.
          </p>
        </motion.div>

        {/* Validation Message */}
        {showValidation && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="p-4 bg-red-50 border-red-200 border">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiAlertCircle} className="w-6 h-6 text-red-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-red-900">Selection Required</h3>
                  <p className="text-sm text-red-800">
                    You must choose either Home Services or Online Services before moving to the next step.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Service Type Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {serviceTypes.map((type, index) => (
            <motion.div
              key={type.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card 
                className={`p-6 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                  selectedType?.id === type.id 
                    ? `ring-2 ring-primary-500 shadow-lg ${type.bgColor}` 
                    : showValidation 
                      ? 'ring-2 ring-red-300 hover:shadow-md' 
                      : 'hover:shadow-md'
                }`}
                onClick={() => handleTypeSelect(type)}
              >
                {/* Header */}
                <div className={`bg-gradient-to-r ${type.color} text-white p-6 rounded-lg mb-6`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={type.icon} className="w-8 h-8" />
                      <h2 className="text-xl font-bold">{type.title}</h2>
                    </div>
                    {selectedType?.id === type.id && (
                      <SafeIcon icon={FiCheckCircle} className="w-6 h-6" />
                    )}
                  </div>
                  <p className="mt-2 text-blue-100">{type.description}</p>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mr-2" />
                    What's Included
                  </h3>
                  <ul className="space-y-2">
                    {type.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-700">
                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Requirements */}
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiUpload} className="w-5 h-5 text-blue-500 mr-2" />
                    Requirements
                  </h3>
                  <ul className="space-y-2">
                    {type.requirements.map((requirement, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mr-2 mt-2 flex-shrink-0"></div>
                        <span>{requirement}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Selection Indicator */}
                {selectedType?.id === type.id && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-primary-50 border border-primary-200 rounded-lg p-3"
                  >
                    <div className="flex items-center text-primary-700">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mr-2" />
                      <span className="font-medium">Selected</span>
                    </div>
                  </motion.div>
                )}
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button
            variant="outline"
            onClick={handleBack}
            className="px-8 py-3"
          >
            Back to Dashboard
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!selectedType}
            className={`px-8 py-3 ${!selectedType ? 'opacity-50 cursor-not-allowed flex items-center justify-center' : 'flex items-center justify-center'}`}
            size="lg"
          >
            Continue to Services
            <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
          </Button>
        </motion.div>

        {/* Information Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-8"
        >
          <Card className="p-6 bg-blue-50 border-blue-200">
            <div className="flex items-start space-x-3">
              <SafeIcon icon={FiIcons.FiInfo} className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Important Information</h3>
                <ul className="space-y-1 text-sm text-blue-800">
                  <li>• You can change your service type later in the application process</li>
                  <li>• Home Services require scheduling and operator availability</li>
                  <li>• Online Services require document scanning or digital copies</li>
                  <li>• Both service types offer the same quality and accuracy guarantee</li>
                </ul>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ServiceTypeSelection;
